import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import SafeIcon from '../common/SafeIcon';
import LogoutConfirmModal from '../components/common/LogoutConfirmModal';
import supabase from '../config/supabase';
import {
  FiSettings, FiUser, FiLock, FiSave,
  FiEye, FiEyeOff, FiCheck, FiX,
  FiAlertCircle, FiLogOut, FiLoader
} from 'react-icons/fi';

// ✅ 模組層級：不依賴 t 的靜態常數
const PROTECTED_PAGES = ['/dashboard', '/settings', '/pet/', '/profile'];

// ✅ 模組層級：靜態動畫設定
const SLIDE_IN = { initial: { opacity: 0, x: 20 }, animate: { opacity: 1, x: 0 }, transition: { duration: 0.3 } };

const SettingsPage = () => {
  const { t } = useLanguage();
  const { user, signOut, isLoggingOut } = useAuth();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('account');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [initialLoad, setInitialLoad] = useState(true);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [accountData, setAccountData] = useState({ firstName: '', lastName: '', email: '', phone: '' });
  const [passwordData, setPasswordData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });

  // ✅ tabs 依賴 t，用 useMemo 避免每次渲染重建
  const tabs = useMemo(() => [
    { id: 'account',  name: t('pages.accountSettings'),  icon: FiUser },
    { id: 'password', name: t('pages.changePassword'),    icon: FiLock },
  ], [t]);

  useEffect(() => {
    if (!user) return;
    supabase.auth.getUser().then(({ data: { user: currentUser }, error }) => {
      if (error) {
        console.error('獲取用戶資料失敗:', error);
        showSaveMessage(t('pages.fetchError'), true);
        return;
      }
      if (currentUser) {
        const metadata = currentUser.user_metadata || {};
        setAccountData({
          firstName: metadata.first_name || metadata.firstName || '',
          lastName:  metadata.last_name  || metadata.lastName  || '',
          email:     currentUser.email || '',
          phone:     metadata.phone || '',
        });
      }
    }).catch(err => {
      console.error('載入用戶資料時發生錯誤:', err);
      showSaveMessage(t('pages.fetchError'), true);
    }).finally(() => {
      setInitialLoad(false);
    });
  }, [user, t]);

  const handleAccountChange = useCallback((e) => {
    const { name, value } = e.target;
    setAccountData(prev => ({ ...prev, [name]: value }));
  }, []);

  const handlePasswordChange = useCallback((e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));
  }, []);

  const showSaveMessage = useCallback((message, isError = false) => {
    setSaveMessage({ text: message, isError });
    setTimeout(() => setSaveMessage(''), 4000);
  }, []);

  const handleSaveAccount = useCallback(async () => {
    if (!user) { showSaveMessage(t('pages.userNotFound'), true); return; }
    setIsSaving(true);
    try {
      const { error } = await supabase.auth.updateUser({
        data: {
          first_name: accountData.firstName.trim(),
          lastName:   accountData.lastName.trim(),
          firstName:  accountData.firstName.trim(),
          last_name:  accountData.lastName.trim(),
          phone:      accountData.phone.trim(),
        },
      });
      if (error) {
        console.error('更新帳戶資料失敗:', error);
        let msg = error.message;
        if (error.message.includes('rate limit')) msg = t('pages.rateLimitError');
        else if (error.message.includes('network')) msg = t('pages.networkError');
        showSaveMessage(msg, true);
        return;
      }
      showSaveMessage(t('pages.accountUpdated'));
    } catch (error) {
      console.error('保存帳戶資料時發生錯誤:', error);
      showSaveMessage(t('pages.updateError'), true);
    } finally {
      setIsSaving(false);
    }
  }, [user, accountData, t, showSaveMessage]);

  const handleSavePassword = useCallback(async () => {
    if (!user) { showSaveMessage(t('pages.userNotFound'), true); return; }
    if (passwordData.newPassword !== passwordData.confirmPassword) { showSaveMessage(t('pages.passwordMismatch'), true); return; }
    if (passwordData.newPassword.length < 6) { showSaveMessage(t('pages.passwordTooShort'), true); return; }
    if (!passwordData.currentPassword) { showSaveMessage(t('pages.currentPasswordRequired'), true); return; }

    setIsSaving(true);
    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email, password: passwordData.currentPassword,
      });
      if (signInError) {
        console.error('當前密碼驗證失敗:', signInError);
        showSaveMessage(t('pages.currentPasswordIncorrect'), true);
        return;
      }
      const { error } = await supabase.auth.updateUser({ password: passwordData.newPassword });
      if (error) {
        console.error('密碼更新失敗:', error);
        let msg = error.message;
        if (error.message.includes('rate limit')) msg = t('pages.rateLimitError');
        else if (error.message.includes('same password')) msg = t('pages.samePasswordError');
        showSaveMessage(msg, true);
        return;
      }
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setShowCurrentPassword(false);
      setShowNewPassword(false);
      setShowConfirmPassword(false);
      showSaveMessage(t('pages.passwordUpdated'));
    } catch (error) {
      console.error('更新密碼時發生錯誤:', error);
      showSaveMessage(t('pages.updateError'), true);
    } finally {
      setIsSaving(false);
    }
  }, [user, passwordData, t, showSaveMessage]);

  const handleLogoutClick = useCallback(() => setShowLogoutModal(true), []);
  const handleCancelLogout = useCallback(() => setShowLogoutModal(false), []);
  const handleConfirmLogout = useCallback(async () => {
    await signOut(location.pathname);
    setShowLogoutModal(false);
  }, [signOut, location.pathname]);

  const getLogoutMessage = useCallback(() => {
    const isProtected = PROTECTED_PAGES.some(path => location.pathname.startsWith(path));
    return isProtected ? t('pages.redirectingToLogin') : t('pages.refreshingPage');
  }, [location.pathname, t]);

  const isPasswordUpdateEnabled = useCallback(() => (
    !isSaving &&
    passwordData.currentPassword.trim() !== '' &&
    passwordData.newPassword.trim() !== '' &&
    passwordData.confirmPassword.trim() !== '' &&
    passwordData.newPassword === passwordData.confirmPassword &&
    passwordData.newPassword.length >= 6 &&
    /[A-Z]/.test(passwordData.newPassword)
  ), [isSaving, passwordData]);

  if (initialLoad) {
    return (
      <div className="min-h-screen bg-neutral-50 pt-20 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-neutral-600">{t('pages.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 pt-20">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div className="mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-3xl font-bold text-neutral-800 mb-2 flex items-center">
                <SafeIcon icon={FiSettings} className="mr-3 text-primary-600" />
                {t('pages.settingsTitle')}
              </h1>
              <p className="text-neutral-600">{t('pages.settingsDescription')}</p>
            </div>
            <button
              onClick={handleLogoutClick} disabled={isLoggingOut}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-200 ${isLoggingOut ? 'bg-red-100 text-red-400 cursor-not-allowed' : 'bg-red-100 text-red-700 hover:bg-red-200 hover:scale-105'}`}
            >
              <SafeIcon icon={isLoggingOut ? FiLoader : FiLogOut} className={`h-4 w-4 ${isLoggingOut ? 'animate-spin' : ''}`} />
              <span>{isLoggingOut ? t('pages.loggingOut') : t('pages.logout')}</span>
            </button>
          </div>

          {saveMessage && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
              className={`mb-6 p-4 rounded-lg flex items-center ${saveMessage.isError ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-green-50 text-green-700 border border-green-200'}`}>
              <SafeIcon icon={saveMessage.isError ? FiAlertCircle : FiCheck} className="mr-2 flex-shrink-0" />
              <span>{saveMessage.text}</span>
            </motion.div>
          )}

          <div className="bg-white rounded-xl shadow-smooth overflow-hidden border border-neutral-100">
            <div className="flex flex-col lg:flex-row">
              <div className="lg:w-1/4 bg-neutral-50 border-r border-neutral-100">
                <nav className="p-4 space-y-1">
                  {tabs.map((tab) => (
                    <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                      className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-all duration-200 ${activeTab === tab.id ? 'bg-primary-500 text-white shadow-md' : 'text-neutral-700 hover:bg-neutral-100 hover:text-primary-600'}`}>
                      <SafeIcon icon={tab.icon} className="text-lg" />
                      <span className="font-medium">{tab.name}</span>
                    </button>
                  ))}
                </nav>
              </div>

              <div className="lg:w-3/4 p-6">
                {activeTab === 'account' && (
                  <motion.div {...SLIDE_IN}>
                    <h2 className="text-2xl font-semibold text-neutral-800 mb-6 flex items-center">
                      <SafeIcon icon={FiUser} className="mr-2 text-primary-600" />
                      {t('pages.accountSettings')}
                    </h2>
                    <p className="text-neutral-600 mb-6">{t('pages.accountInfoSubtitle')}</p>
                    <div className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-neutral-700 mb-2">{t('pages.firstName')}</label>
                          <input type="text" name="firstName" value={accountData.firstName} onChange={handleAccountChange}
                            className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white text-neutral-900 transition-all"
                            placeholder={t('pages.firstName')} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-neutral-700 mb-2">{t('pages.lastName')}</label>
                          <input type="text" name="lastName" value={accountData.lastName} onChange={handleAccountChange}
                            className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white text-neutral-900 transition-all"
                            placeholder={t('pages.lastName')} />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-2">{t('pages.email')}</label>
                        <input type="email" name="email" value={accountData.email} disabled
                          className="w-full px-4 py-3 border border-neutral-300 rounded-lg bg-neutral-100 text-neutral-600 cursor-not-allowed"
                          placeholder={t('pages.emailPlaceholder')} />
                        <p className="text-xs text-neutral-500 mt-1">{t('pages.emailCannotBeChanged')}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-2">{t('pages.phoneNumber')}</label>
                        <input type="tel" name="phone" value={accountData.phone} onChange={handleAccountChange}
                          className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white text-neutral-900 transition-all"
                          placeholder={t('pages.phonePlaceholder')} />
                      </div>
                      <div className="flex flex-wrap gap-3 pt-4">
                        <button onClick={handleSaveAccount} disabled={isSaving}
                          className="flex items-center space-x-2 px-6 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-medium">
                          <SafeIcon icon={FiSave} />
                          <span>{isSaving ? t('pages.saving') : t('pages.saveChanges')}</span>
                        </button>
                      </div>
                    </div>
                  </motion.div>
                )}

                {activeTab === 'password' && (
                  <motion.div {...SLIDE_IN}>
                    <h2 className="text-2xl font-semibold text-neutral-800 mb-6 flex items-center">
                      <SafeIcon icon={FiLock} className="mr-2 text-primary-600" />
                      {t('pages.changePassword')}
                    </h2>
                    <p className="text-neutral-600 mb-6">{t('pages.changePasswordSubtitle')}</p>
                    <div className="space-y-6 max-w-md">
                      {[
                        { key: 'currentPassword', show: showCurrentPassword, setShow: setShowCurrentPassword, label: t('pages.currentPassword'), placeholder: t('pages.enterCurrentPassword') },
                        { key: 'newPassword',      show: showNewPassword,     setShow: setShowNewPassword,     label: t('pages.newPassword'),     placeholder: t('pages.enterNewPassword') },
                        { key: 'confirmPassword',  show: showConfirmPassword,  setShow: setShowConfirmPassword,  label: t('pages.confirmPassword'),  placeholder: t('pages.confirmNewPassword') },
                      ].map(({ key, show, setShow, label, placeholder }) => (
                        <div key={key}>
                          <label className="block text-sm font-medium text-neutral-700 mb-2">{label}</label>
                          <div className="relative">
                            <input type={show ? 'text' : 'password'} name={key} value={passwordData[key]} onChange={handlePasswordChange}
                              className="w-full px-4 py-3 pr-12 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white text-neutral-900 transition-all"
                              placeholder={placeholder} />
                            <button type="button" onClick={() => setShow(v => !v)}
                              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-neutral-400 hover:text-neutral-600 transition-colors">
                              <SafeIcon icon={show ? FiEyeOff : FiEye} />
                            </button>
                          </div>
                        </div>
                      ))}

                      <div className="bg-primary-50 p-4 rounded-lg border border-primary-200">
                        <h3 className="font-medium text-primary-800 mb-2">{t('pages.passwordRequirements')}</h3>
                        <ul className="text-sm text-primary-700 space-y-1">
                          <li className="flex items-center">
                            <SafeIcon icon={passwordData.newPassword.length >= 6 ? FiCheck : FiX} className={`mr-2 ${passwordData.newPassword.length >= 6 ? 'text-green-600' : 'text-red-600'}`} />
                            {t('pages.passwordMinLength')}
                          </li>
                          <li className="flex items-center">
                            <SafeIcon icon={/[A-Z]/.test(passwordData.newPassword) ? FiCheck : FiX} className={`mr-2 ${/[A-Z]/.test(passwordData.newPassword) ? 'text-green-600' : 'text-red-600'}`} />
                            {t('pages.passwordMixCase')}
                          </li>
                        </ul>
                      </div>

                      {passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword && (
                        <div className="bg-red-50 p-3 rounded-lg border border-red-200">
                          <div className="flex items-center text-red-700">
                            <SafeIcon icon={FiAlertCircle} className="mr-2 flex-shrink-0" />
                            <span className="text-sm">{t('pages.passwordMismatch')}</span>
                          </div>
                        </div>
                      )}

                      {passwordData.confirmPassword && passwordData.newPassword === passwordData.confirmPassword && passwordData.newPassword.length >= 6 && /[A-Z]/.test(passwordData.newPassword) && (
                        <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                          <div className="flex items-center text-green-700">
                            <SafeIcon icon={FiCheck} className="mr-2 flex-shrink-0" />
                            <span className="text-sm">{t('pages.passwordsMatch')}</span>
                          </div>
                        </div>
                      )}

                      <button onClick={handleSavePassword} disabled={!isPasswordUpdateEnabled()}
                        className="flex items-center space-x-2 px-6 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-medium">
                        <SafeIcon icon={FiSave} />
                        <span>{isSaving ? t('pages.updating') : t('pages.updatePasswordButton')}</span>
                      </button>
                    </div>
                  </motion.div>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      <LogoutConfirmModal
        isOpen={showLogoutModal}
        onClose={handleCancelLogout}
        onConfirm={handleConfirmLogout}
        isLoggingOut={isLoggingOut}
      />

      <AnimatePresence>
        {isLoggingOut && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-2xl max-w-sm w-full mx-4">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{t('pages.loggingOut')}</h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm">{getLogoutMessage()}</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SettingsPage;